import { Box, Checkbox, Stack } from "@mui/material";
import type React from "react";
import { forwardRef, useMemo } from "react";
import { VirtuosoGrid, type VirtuosoGridHandle } from "react-virtuoso";
import { GridVirtualizedSelectListContainer } from "./GridVirtualizedSelectListContainer";
import { selectableItemContent } from "./SelectableRow";
import { ListVirtualizedSelectContextProvider, context } from "./context";
import { useGridVirtualizedSelect } from "./logics";
import { GridVirtualizedSelectItemContainer } from "./styles";
import type { IGridVirtualizedSelectProps } from "./types";

export const GridVirtualizedSelect = forwardRef(function GridVirtualizedSelect<DataGetId, Data extends DataGetId>(
  {
    data,
    selectedItems: selectedItemsProps,
    disabledItems,
    onChange,
    headerContent,
    itemContent,
    emptyContent,
    getId,
    hideCheckbox,
    ...props
  }: IGridVirtualizedSelectProps<DataGetId, Data>,
  ref: React.Ref<VirtuosoGridHandle>
) {
  const { selectedItems, isChecked, isIndeterminate, setSelectedItems, handleSelectAll, handleSelect } =
    useGridVirtualizedSelect(data, selectedItemsProps, disabledItems, onChange, getId);

  const contextValue = useMemo(
    () => ({
      itemContent,
      getId,
      selectedItems,
      handleSelect,
      disabledItems,
    }),
    [itemContent, getId, selectedItems, handleSelect, disabledItems]
  );

  if (emptyContent && data.length === 0) {
    return (
      <Box
        sx={[
          {
            flex: "auto",
          },
          (theme) => ({ padding: theme.spacing(1) }),
        ]}
      >
        {emptyContent()}
      </Box>
    );
  }

  return (
    <ListVirtualizedSelectContextProvider value={contextValue}>
      <Stack
        sx={[
          {
            width: "100%",
          },
          () => ({ flex: 1 }),
        ]}
      >
        <Stack
          direction="row"
          spacing={2}
          {...(!hideCheckbox ? { sx: () => ({ marginLeft: "-1px" }) } : {})}
          sx={{
            alignItems: "center",
          }}
        >
          {!hideCheckbox && (
            <Checkbox
              checked={isChecked}
              indeterminate={isIndeterminate}
              onChange={handleSelectAll}
              sx={(theme) => ({ padding: theme.spacing(1) })}
            />
          )}
          <Box
            {...(hideCheckbox ? { sx: (theme) => ({ padding: theme.spacing(1) }) } : {})}
            sx={{
              flex: "auto",
            }}
          >
            {headerContent(selectedItems, setSelectedItems)}
          </Box>
        </Stack>
        <VirtuosoGrid
          totalCount={data.length}
          itemContent={(index) => selectableItemContent(index, data[index], contextValue)}
          context={context}
          components={{ Item: GridVirtualizedSelectItemContainer, List: GridVirtualizedSelectListContainer }}
          ref={ref}
          {...props}
        />
      </Stack>
    </ListVirtualizedSelectContextProvider>
  );
});
